


import { Component, Vue } from "vue-property-decorator";

import { CustomNotification } from "@Internal/Models";

import { ConsoleUsers } from "App";
import { Field, Role, User } from "App/Models";
import { $t, EventBus } from "App/IOC";

import { Dictionary } from "App/Contracts";
import { Option } from "Web/Contracts";

import SimpleSelect from "Web/Components/V2/Select/Simple.vue";
import PhoneNumberField from "Web/Components/PhoneNumber.vue";
import Form from "Web/Components/Form/Index.vue";
import FormField from "Web/Components/Form/Field.vue";
import Fieldset from "Web/Components/Form/Fieldset.vue";

type Section = "profil" | "role";

@Component({
    components: {
        SimpleSelect,
        PhoneNumberField,
        Form,
        Field: FormField,
        Fieldset
    }
})
export default class SingleConsoleUserView extends Vue {
    private user?: User;
    private roles: Role[] = [];
    private selectedRole?: Role;

    private loading: boolean = true;

    /* Which sub-page of the fiche is shown — mirrors the "Votre profil" sub-sidebar (record loaded
       once, the left links just switch the visible block; no nested routes). */
    private section: Section = "profil";

    /* Identity fields rendered through the shared Form/Field framework — same look (pencil +
       underline, bottom save bar) as the Customers fiche. Email is read-only (changing it breaks
       the login); Phone goes through the country-flag PhoneNumber widget. */
    private fields: Dictionary<Field> = {};

    /* Bumped after a successful save to remount the Form with the freshly reloaded record, so its
       internal "changed" state resets and the save bar disappears. */
    private formKey: number = 0;

    private select( section: Section ): void {
        this.section = section;
    }

    private linkClass( section: Section ): string {
        return this.section === section
            ? "l-navigable__link router-link-active"
            : "l-navigable__link";
    }

    private get isPending(): boolean {
        return this.user !== undefined && this.user.State === "pending";
    }

    private get isDisabled(): boolean {
        return this.user !== undefined && this.user.State === "disabled";
    }

    /* A Super Admin (protected role) can never be disabled. */
    private get isSuperAdmin(): boolean {
        return this.selectedRole !== undefined && this.selectedRole.IsProtected === true;
    }

    private get stateLabel(): string {
        return this.user !== undefined ? ConsoleUsers.StateLabel( this.user.State ) : "";
    }

    /* Title shows the person's first + last name; email is only a fallback for an invited account
       that has no name yet. Built from the fields directly (not the User.Name getter, which the
       deserialized record may not carry). */
    private get displayName(): string {
        if( this.user === undefined )
            return "";

        const name = [ this.user.FirstName, this.user.LastName ]
            .filter( part => part !== undefined && part !== "" )
            .join( " " );

        return name !== "" ? name : ( this.user.Email || "" );
    }

    private get roleOptions(): Option[] {
        return this.roles as unknown as Option[];
    }

    private async created(): Promise<void> {
        this.fields = {
            FirstName: Field.Text( "FirstName", $t( "CONSOLE_USERS_fieldFirstName" ) ),
            LastName: Field.Text( "LastName", $t( "CONSOLE_USERS_fieldLastName" ) ),
            Email: Field.Text( "Email", $t( "CONSOLE_USERS_fieldEmail" ) ),
            Phone: Field.Text( "Phone", $t( "CONSOLE_USERS_fieldPhone" ) )
        };

        const [ roles ] = await ConsoleUsers.GetRoles();
        this.roles = roles;

        await this.load();
    }

    private async load(): Promise<void> {
        const [ user, error ] = await ConsoleUsers.Get( Number( this.$route.params.id ) );

        if( error !== undefined || user === undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.user = user;
        this.selectedRole = this.roles.find( role => role.Id === user.RoleId );
        this.loading = false;

        if( this.displayName !== "" )
            document.title = this.displayName;
    }

    /* Build the update payload from a clone of the loaded user, overwriting ONLY the editable
       identity fields. The base service diffs original vs updated and sends the difference, forcing
       any `undefined` to `null`. Passing the Form's own record risks a stray `undefined` on a field
       it doesn't own (Role/RoleId/State) being sent as `null`, which corrupts the row and makes
       every later user read 500. Cloning this.user guarantees those fields equal the original and
       are never part of the diff. A shallow spread is used (not Model.Copy()): the Internal/Users
       payload carries no $Model annotation, so the deserialized record is a plain object without
       the Copiable prototype. */
    private async save( record: User ): Promise<void> {
        if( this.user === undefined )
            return;

        const updated = { ...this.user } as User;
        updated.FirstName = record.FirstName;
        updated.LastName = record.LastName;
        updated.Phone = record.Phone;

        const [ , error ] = await ConsoleUsers.Update( this.user, updated );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.notify( $t( "CONSOLE_USERS_edited" ) );

        /* Reload first so the remounted Form copies the freshly saved record (bumping the key
           before the async reload would remount it on the stale user → input shows old value). */
        await this.load();
        this.formKey++;
    }

    private notify( message: string ): void {
        EventBus.Publish( "Notification", CustomNotification.Success(
            $t( "CONSOLE_USERS_title" ), message
        ));
    }

    private async assignRole( role: Role ): Promise<void> {
        if( this.user === undefined )
            return;

        const error = await ConsoleUsers.AssignRole( this.user.Id, role.Id );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.selectedRole = role;
        this.notify( $t( "CONSOLE_USERS_roleAssigned" ) );
        this.load();
    }

    private disable(): void {
        if( this.user === undefined )
            return;

        EventBus.Publish( "Confirmation", {
            Props: {
                Message: this.$t( "CONSOLE_USERS_disableConfirm" ),
                Actions: [{
                    Id: "disable",
                    Name: this.$t( "CONSOLE_USERS_actionDisable" ),
                    Active: true
                }]
            },
            Events: { Action: () => this.runDisable() }
        });
    }

    private async runDisable(): Promise<void> {
        if( this.user === undefined )
            return;

        const error = await ConsoleUsers.Disable( this.user.Id );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.notify( $t( "CONSOLE_USERS_disabled" ) );
        this.load();
    }

    private async reactivate(): Promise<void> {
        if( this.user === undefined )
            return;

        const error = await ConsoleUsers.Reactivate( this.user.Id );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.notify( $t( "CONSOLE_USERS_reactivated" ) );
        this.load();
    }

    private async reinvite(): Promise<void> {
        if( this.user === undefined )
            return;

        const error = await ConsoleUsers.Reinvite( this.user.Id );

        if( error !== undefined ) {
            EventBus.Publish( "Error", error );
            return;
        }

        this.notify( $t( "CONSOLE_USERS_reinvited" ) );
        this.load();
    }
}
